import {
    APP_ID,
    ApplicationConfig,
    ErrorHandler,
    forwardRef,
    importProvidersFrom,
    provideZoneChangeDetection
} from '@angular/core';
import {
    provideRouter,
    RouteReuseStrategy,
    UrlSerializer,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withRouterConfig
} from '@angular/router';

import { routes } from './app-routing';
import { provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateLoaderService } from '@core/services/translate-loader.service';
import { Breakpoint, BreakpointModule } from '@libs/breakpoints';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { CacheInterceptor } from '@core/interceptors/cache.interceptor';
import { ApiInterceptor } from '@core/interceptors/api.interceptor';
import { LoginRequiredInterceptor } from '@core/interceptors/login-required.interceptor';
import { AuthenticationInterceptor } from '@core/interceptors/authentication.interceptor';
import { ProgressInterceptor } from '@core/interceptors/progress.interceptor';
import { HttpTimeoutInterceptor } from '@core/interceptors/http-timeout.interceptor';
import { CustomErrorHandlerService } from '@core/services/custom-error-handler';
import { AUTHENTICATION_SERVICE_TOKEN, BUILD_NUMBER, SENTRY } from './app.tokens';
import * as BrowserSentry from '@sentry/browser';
import { provideAnimations } from '@angular/platform-browser/animations';

import { OverlayModule } from '@libs/overlay';
import { LowerCaseUrlSerializer } from '@core/serializers/lower-case-url-serializer';
import { NoReuseStrategy } from '@libs/routing/reuse-strategies/no-reuse.strategy';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import { environment } from '@environments/environment';

export const provideTranslation = () => ({
    loader: {
        provide: TranslateLoader,
        useClass: TranslateLoaderService
    }
});

export const breakpointConfiguration = [
    // Mobile
    Breakpoint.fromQuery('mobile', '(max-width: 747px)'),
    // Mobile-large
    Breakpoint.fromQuery('mobile-large', '(min-width: 480px) and (max-width: 747px)'),
    // Tablet
    Breakpoint.fromQuery('tablet', '(min-width: 748px) and (max-width: 1003px)'),
    // Desktop
    Breakpoint.fromQuery('desktop', '(min-width: 1004px)'),
    // Full width
    Breakpoint.fromQuery('full', '(min-width: 1440px)')
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        provideRouter(
            routes,
            withEnabledBlockingInitialNavigation(),
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }),
            withRouterConfig({
                urlUpdateStrategy: 'deferred',
                paramsInheritanceStrategy: 'always',
                onSameUrlNavigation: 'reload'
            })
        ),
        provideClientHydration(
            withHttpTransferCacheOptions({
                includeHeaders: ['www-authenticate']
            })
        ),
        provideZoneChangeDetection({ eventCoalescing: true }),
        importProvidersFrom([
            TranslateModule.forRoot(provideTranslation()),
            BreakpointModule.withConfiguration(breakpointConfiguration),
            OverlayModule
        ]),
        {
            provide: APP_ID,
            useValue: 'kopandkande-spa'
        },
        {
            provide: PRECONNECT_CHECK_BLOCKLIST,
            useValue: [`https://media.${environment.apiDomain}`, `https://umbraco-netcore.${environment.apiDomain}`]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoginRequiredInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ProgressInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpTimeoutInterceptor,
            multi: true
        },
        {
            provide: AUTHENTICATION_SERVICE_TOKEN,
            useExisting: forwardRef(() => AuthenticationService)
        },
        {
            provide: ErrorHandler,
            useClass: CustomErrorHandlerService
        },
        {
            provide: SENTRY,
            useValue: BrowserSentry
        },
        {
            provide: BUILD_NUMBER,
            useValue: '7560' // Will be replaced by PowerShell script in DevOps.
        },
        {
            provide: RouteReuseStrategy,
            useClass: NoReuseStrategy
        },
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer
        }
    ]
};
